import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import AirportShuttleRoundedIcon from '@mui/icons-material/AirportShuttleRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import { lazy } from 'react';
import { PERMISSIONS, holdsPermission } from '@/api/permissions';
import { paths } from '@/routes/paths';
import { RequirePermission } from '@/routes/guards';
import type { ModuleManifest } from './manifest';

const DriversPage = lazy(() => import('@/features/fleet/DriversPage'));
const DriverDetailPage = lazy(() => import('@/features/fleet/DriverDetailPage'));
const VehiclesPage = lazy(() => import('@/features/fleet/VehiclesPage'));
const GpsDevicesPage = lazy(() => import('@/features/fleet/GpsDevicesPage'));
// Its own chunk, and mapbox-gl inside it is imported later still, on mount.
const FleetMapPage = lazy(() => import('@/features/tracking/FleetMapPage'));
const TrackingUnitsPage = lazy(() => import('@/features/tracking/TrackingUnitsPage'));

/**
 * The fleet is the platform's; there is no tenant fleet in this version.
 *
 * DELIVERY_MANAGE is listed beside DRIVER_MANAGE / VEHICLE_MANAGE because the
 * assignment screen reads drivers and vehicles, and the routes have always
 * allowed it — the sidebar used not to, which drew fewer doors than the router
 * opened.
 *
 * Tracking lives here because it is platform-only and so is this module
 * (owner, 2026-10-20: platform fleet staff only). TRACKING_READ opens the
 * module on its own, so a view-only dispatcher role built from it alone lands
 * on the map. The two tracking entries `require` the deployment to have
 * tracking at all, asked of `GET /tracking/availability`, so a console without
 * Wialon draws neither.
 */
export const fleetModule: ModuleManifest = {
  key: 'fleet',
  titleKey: 'modules.fleet.title',
  descriptionKey: 'modules.fleet.description',
  // Not the lorry: that is logistics' tile, and two identical tiles read as one.
  icon: AirportShuttleRoundedIcon,
  prefix: '/fleet',
  audience: 'platform',
  anyOf: [
    PERMISSIONS.DRIVER_MANAGE,
    PERMISSIONS.VEHICLE_MANAGE,
    PERMISSIONS.DELIVERY_MANAGE,
    PERMISSIONS.TRACKING_READ,
  ],
  sections: [
    {
      key: 'fleet',
      items: [
        {
          key: 'fleetMap',
          labelKey: 'nav.fleetMap',
          to: paths.fleetMap,
          icon: <MapRoundedIcon />,
          anyOf: [PERMISSIONS.TRACKING_READ],
          requires: 'tracking',
        },
        {
          key: 'drivers',
          labelKey: 'nav.drivers',
          to: paths.drivers,
          icon: <BadgeRoundedIcon />,
          anyOf: [PERMISSIONS.DRIVER_MANAGE, PERMISSIONS.DELIVERY_MANAGE],
        },
        {
          key: 'vehicles',
          labelKey: 'nav.vehicles',
          to: paths.vehicles,
          icon: <DirectionsCarRoundedIcon />,
          anyOf: [PERMISSIONS.VEHICLE_MANAGE, PERMISSIONS.DELIVERY_MANAGE],
        },
        {
          key: 'gpsDevices',
          labelKey: 'nav.gpsDevices',
          to: paths.gpsDevices,
          icon: <GpsFixedRoundedIcon />,
          anyOf: [PERMISSIONS.VEHICLE_MANAGE, PERMISSIONS.DELIVERY_MANAGE],
        },
        {
          key: 'trackingUnits',
          labelKey: 'nav.trackingUnits',
          to: paths.trackingUnits,
          icon: <LinkRoundedIcon />,
          anyOf: [PERMISSIONS.VEHICLE_MANAGE],
          requires: 'tracking',
        },
      ],
    },
  ],
  routes: [
    // DELIVERY_MANAGE may read the fleet: the assignment screen needs it.
    {
      element: (
        <RequirePermission
          permission={[PERMISSIONS.DRIVER_MANAGE, PERMISSIONS.DELIVERY_MANAGE]}
          mode="any"
        />
      ),
      children: [
        { path: paths.drivers, element: <DriversPage /> },
        { path: paths.driver(), element: <DriverDetailPage /> },
      ],
    },
    {
      element: (
        <RequirePermission
          permission={[PERMISSIONS.VEHICLE_MANAGE, PERMISSIONS.DELIVERY_MANAGE]}
          mode="any"
        />
      ),
      children: [
        { path: paths.vehicles, element: <VehiclesPage /> },
        { path: paths.gpsDevices, element: <GpsDevicesPage /> },
      ],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.TRACKING_READ]} />,
      children: [{ path: paths.fleetMap, element: <FleetMapPage /> }],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.VEHICLE_MANAGE]} />,
      children: [{ path: paths.trackingUnits, element: <TrackingUnitsPage /> }],
    },
  ],
  landing: (viewer) =>
    holdsPermission(viewer.permissions, PERMISSIONS.DRIVER_MANAGE) ||
    holdsPermission(viewer.permissions, PERMISSIONS.DELIVERY_MANAGE)
      ? paths.drivers
      : holdsPermission(viewer.permissions, PERMISSIONS.VEHICLE_MANAGE)
        ? paths.vehicles
        : paths.fleetMap,
};
